export type AiEvalPresignedUrl = {
  uploadUrl: string
  key: string
  fileName: string
}

export type UploadAiEvalImagesResponse = {
  juid: string
  sessionId: string
  urls: AiEvalPresignedUrl[]
  expiresIn: number
}

export type VinCandidate = {
  value: string
  confidence?: number
  sourceImageKey?: string
}

export type YmmCandidate = {
  year: number
  make: string
  model: string
  confidence?: number
  sourceImageKey?: string
}

export type AiVehicleIdentityResponse = {
  sessionId: string
  vinCandidates: VinCandidate[]
  ymmCandidates: YmmCandidate[]
  warnings?: string[]
}

export type PromoteAiEvalImagesResponse = {
  juid: string
  sessionId: string
  vin: string
  promoted: Array<{ fromKey: string; toKey: string; publicUrl: string; fileName: string }>
}

function baseUrl(endpoint: string): string {
  return String(endpoint || '').replace(/\/+$/g, '')
}

export async function requestAiEvalUploadUrls(params: {
  endpoint: string
  sessionId: string
  imageCount: number
  fileNames?: string[]
  sessionToken: string
}): Promise<UploadAiEvalImagesResponse> {
  const url = `${baseUrl(params.endpoint)}/upload-ai-eval-images`
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.sessionToken}`,
    },
    body: JSON.stringify({
      sessionId: params.sessionId,
      imageCount: params.imageCount,
      fileNames: params.fileNames,
    }),
  })

  const json = (await resp.json().catch(() => ({}))) as any
  if (!resp.ok) {
    throw new Error(json?.message || json?.error || `Failed to request upload URLs (${resp.status})`)
  }
  return json as UploadAiEvalImagesResponse
}

export async function uploadFilesToPresignedUrls(params: {
  urls: AiEvalPresignedUrl[]
  files: File[]
}): Promise<void> {
  if (params.urls.length !== params.files.length) {
    throw new Error('Upload URL count mismatch')
  }

  for (let i = 0; i < params.files.length; i++) {
    const u = params.urls[i]
    const file = params.files[i]
    const putResp = await fetch(u.uploadUrl, {
      method: 'PUT',
      body: file,
    })
    if (!putResp.ok) {
      throw new Error(`Failed to upload ${u.fileName}`)
    }
  }
}

export async function evaluateVehicleIdentity(params: {
  endpoint: string
  sessionId: string
  imageKeys: string[]
  sessionToken: string
}): Promise<AiVehicleIdentityResponse> {
  const url = `${baseUrl(params.endpoint)}/ai-vehicle-identity`
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.sessionToken}`,
    },
    body: JSON.stringify({
      sessionId: params.sessionId,
      imageKeys: params.imageKeys,
    }),
  })

  const json = (await resp.json().catch(() => ({}))) as any
  if (!resp.ok) {
    throw new Error(json?.message || json?.error || `AI evaluation failed (${resp.status})`)
  }
  return json as AiVehicleIdentityResponse
}

export async function promoteAiEvalImages(params: {
  endpoint: string
  sessionId: string
  vin: string
  imageKeys: string[]
  fileNames?: string[]
  sessionToken: string
}): Promise<PromoteAiEvalImagesResponse> {
  const url = `${baseUrl(params.endpoint)}/ai-eval/promote-images`
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.sessionToken}`,
    },
    body: JSON.stringify({
      sessionId: params.sessionId,
      vin: params.vin,
      imageKeys: params.imageKeys,
      fileNames: params.fileNames,
      deleteSource: true,
    }),
  })

  const json = (await resp.json().catch(() => ({}))) as any
  if (!resp.ok) {
    throw new Error(json?.message || json?.error || `Failed to promote images (${resp.status})`)
  }
  return json as PromoteAiEvalImagesResponse
}

